import {
  Box,
  Button,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {ClientDesc, ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {Navigable, State} from '@synako/halitrek';
import {HaliRenderOne, useHalState} from '@synako/haligator';
import React, {
  CSSProperties,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {FacetResult, Result, Results} from '@orama/orama';
import {Article, HitItemCatalogue} from 'api-types/ffconnect';
import {Trans, useTranslation} from 'react-i18next';
import {DisplayLabels} from './DisplayLabels';
import {SwitchIcon} from '@/components/svg/SwitchIcon';
import {CartIcon} from '@/components/svg/CartIcon';
import {CadencierIcon} from '@/components/svg/CadencierIcon';
import {ListIcon} from '@/components/svg/ListIcon';
import {CheckIcon, CloseIcon, SearchIcon} from '@chakra-ui/icons';
import {FilterBar} from '@/components/FilterBar/FilterBar';
import {
  SearchClientProvider,
  coerceFiltresItemCatalogue as coerce,
  useGetSelectUniteOptions,
  useLivraisonsPossiblesContext,
  useSearchClient,
} from 'ff-common';
import {useDebouncedChange, useTypedFilters} from '@synako/typed-path';
import {useApi, useClient} from '@/haligator-factory';
import {RemplacerUnArticleCommand} from 'api-types/ffconnect';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';
import {LabelDisponibilite} from '@/domains/catalogue/components/LabelDisponibilite';
import {differenceInDays} from 'date-fns/differenceInDays';
import {ColdIcon} from '@/components/svg/ColdIcon';
import {PromoIcon} from '@/components/svg/PromoIcon';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {routes} from 'ff-common';
import {Link, useParams} from 'react-router-dom';
import {FiltresItemCatalogue} from 'ff-common/src/hooks/search/use-search';
import range from 'lodash-es/range';
import {FixedSizeGrid} from 'react-window';
import {useScrollbarWidth} from '@/chakra/useScrollbarWidth';
import {BreakPointContext} from '@/chakra/BreakpointProvider';
import {DetailsProduitPriseDeCommande} from '@/backoffice/domains/PriseDeCommande/AjoutArticle/DetailsProduitPriseDeCommande';
import {ArticleCodesDisplay} from './ArticleCodesDisplay';
import {PrixBlockUnitaire} from '@/domains/cadencier/components/PrixBlockUnitaire';
import {useBlockScroll} from '@/backoffice/hooks/useBlockScroll';
import {MargesProduit} from '@/domains/ligneProduit/MargesProduit';
import {BoutonDLCStock} from '@/domains/ligneProduit/BoutonDLCStock';
import {useHauteurVignetteRemplacement} from '@/hooks/panier/useVignetteRemplacement';
import {usePeutVoirDlcStock} from '@/hooks/auth/usePermissionsCommerciales';
import {getModeFromUrl} from '@/utils/appMode';
import {TagCadencier} from '@/domains/cadencier/components/TagCadencier';

export type ModaleRemplacementMode =
  | 'replace'
  | 'add-to-list'
  | 'add-to-panier'
  | 'add-to-cadencier';

// Composant interne qui est enveloppé par les contextes nécessaires
const ModaleRemplacementContent = ({
  articleData,
  remplacerSubmit,
  mode = 'replace',
  onListAdd,
  isDisabled,
  onProductClick,
}: {
  articleData: Article;
  remplacerSubmit?: (payload: RemplacerUnArticleCommand) => void;
  mode?: ModaleRemplacementMode;
  onListAdd?: (code_article: string) => Promise<void>;
  isDisabled?: (code_article: string) => boolean;
  onProductClick?: (code_article: string) => void;
}) => {
  const modal = useModal();
  const {t} = useTranslation();

  const params = useParams<{date_de_livraison: string}>();

  const search = useSearchClient();
  const client = useClient();
  // Vue Commerce : Produits marché en tête de la barre, décoché à l'ouverture comme côté client.
  // Présentation seulement — marge et DLC/stock dépendent des permissions.
  const vueCommerce = getModeFromUrl() === 'commercial';
  const [remplacements, setRemplacements] = useState<
    Results<HitItemCatalogue> | undefined
  >(undefined);

  const filtersHook = useTypedFilters<FiltresItemCatalogue>({q: ''}, coerce);
  const debouncedChange = useDebouncedChange(filtersHook, 'q', 300);
  const [hits, setHits] = useState<Results<HitItemCatalogue> | undefined>(
    undefined,
  );

  const hasFiltresHorsTexte = !!(
    filtersHook.params.nature_du_prix ??
    filtersHook.params.labels?.length ??
    filtersHook.params.temperature_livraison ??
    filtersHook.params.famille_fromage?.length ??
    filtersHook.params.type_de_lait?.length ??
    filtersHook.params.traitement_thermique_du_lait?.length ??
    filtersHook.params.produit_marche
  );
  const hasRechercheTexte = !!filtersHook.params.q;

  // Récupéreration du contexte de livraison
  const livraisonContext = useLivraisonsPossiblesContext();
  // Si nous sommes dans le backoffice, le contexte peut être undefined
  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  const date_de_livraison =
    livraisonContext.date_de_livraison ||
    params.date_de_livraison ||
    new Date().toISOString().split('T')[0];
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  const diff = differenceInDays(date_de_livraison, new Date());
  const ApourB = diff <= 1;

  // Cela laisse le temps à la transition de se faire
  const [goRender, setGoRender] = useState(false);
  useEffect(() => {
    if (modal.visible) {
      setTimeout(() => {
        setGoRender(true);
      }, 600);
    }
  }, [modal.visible]);

  useEffect(() => {
    if (!goRender || !search.indexIsBuilt) return;
    let ignore = false;
    const perimetre = {
      precommande: ApourB ? false : undefined,
      afficher_marketplace: false as const,
    };
    if (hasRechercheTexte) {
      // Recherche texte : tout le catalogue FF, avec les filtres éventuels.
      search
        .searchItems({
          nature_du_prix: filtersHook.params.nature_du_prix,
          labels: filtersHook.params.labels,
          temperature_livraison: filtersHook.params.temperature_livraison,
          famille_fromage: filtersHook.params.famille_fromage,
          type_de_lait: filtersHook.params.type_de_lait,
          traitement_thermique_du_lait:
            filtersHook.params.traitement_thermique_du_lait,
          produit_marche: filtersHook.params.produit_marche,
          q: filtersHook.params.q,
          ...perimetre,
        })
        .then((resultats) => {
          if (ignore) return;
          setHits(resultats);
          setRemplacements(undefined);
        });
    } else {
      // Suggestions de remplacement, filtrées localement (ex. Produits marché) —
      // on ne bascule pas sur tout le catalogue, pour garder la pertinence famille/mot directeur.
      search
        .remplacementPour(articleData, {
          enleverPrecommande: ApourB,
        })
        .then((resultats) => {
          if (ignore) return;
          setRemplacements(
            filtrerHitsRemplacement(resultats, filtersHook.params),
          );
        });
      setHits(undefined);
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersHook.params, goRender, search.indexIsBuilt]);

  useBlockScroll(modal.visible);

  const remplacerClick = (
    itemState: State<ItemCatalogue2Desc>,
    // index: number,
  ) => {
    switch (mode) {
      case 'replace':
        remplacerSubmit?.({
          code_article: itemState.data.code,
        });
        break;
      case 'add-to-list':
      case 'add-to-panier': // Mode utilisé pour ajouter à partir de la modale de remplacement dans Mes Produits par saison en mode panier
      case 'add-to-cadencier':
        onListAdd?.(itemState.data.code);
        break;
    }

    modal.remove();
  };

  const hitsToRender = hits?.hits ?? remplacements?.hits ?? [];
  // Recherche texte → `hits` ; sinon suggestions (éventuellement filtrées) → `remplacements`.
  const listeVide = hasRechercheTexte
    ? hits?.hits.length === 0
    : remplacements?.hits.length === 0;
  const messageListeVide = filtersHook.params.produit_marche
    ? t('commande.remplacement_aucun_produit_marche')
    : hasRechercheTexte || hasFiltresHorsTexte
      ? t('commande.remplacement_aucun_filtre')
      : t('commande.remplacement_aucun_article');
  const placeholder = range(0, 6).map((_, i) => (
    <RemplacementItemSkeleton key={i} />
  ));

  const {isMobile} = useContext(BreakPointContext)!;
  const hauteurVignette = useHauteurVignetteRemplacement(isMobile);

  const scollW = useScrollbarWidth();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const usableWidth = boxRef.current?.clientWidth;
  // console.log('usableWidth:', usableWidth);
  // console.log('clientHeight:', boxRef.current?.clientHeight);
  const columnCount = isMobile ? 1 : 3;
  const columnWidth = isMobile
    ? usableWidth
    : Math.floor((usableWidth ?? 100 - scollW) / 3);

  return (
    <Drawer
      placement='right'
      isOpen={modal.visible}
      onClose={() => {
        modal.remove();
      }}
      size='xl'
    >
      <DrawerOverlay />
      <DrawerContent
        position='relative'
        bg='white'
        maxW='740px'
        transition='all 0.2s ease-out'
      >
        <VStack
          height='100%'
          spacing={0}
        >
          <HStack
            p='4'
            bg='blue.main'
            color='white'
            position='sticky'
            top='0'
            zIndex='sticky'
            w='100%'
            spacing={4}
          >
            <VStack
              flex={1}
              alignItems='flex-start'
            >
              <Text>
                <Trans t={t}>
                  {t('commande.remplacer_article', {
                    nom: articleData.tolede.libelle_commercial,
                  })}
                </Trans>
              </Text>
            </VStack>
            <Box>
              <DrawerCloseButton position='static' />
            </Box>
          </HStack>
          <Box
            w='100%'
            mx='auto'
          >
            <VStack
              spacing={0}
              align='stretch'
              w='100%'
            >
              <Box
                bg='gray.light'
                p='5'
              >
                <InputGroup
                  zIndex={1003}
                  bg='white'
                  borderRadius='8px'
                >
                  <InputLeftElement
                    pointerEvents='auto'
                    cursor='pointer'
                  >
                    <SearchIcon />
                  </InputLeftElement>
                  <Input
                    placeholder={t('commande.chercher_tout_catalogue')}
                    pr={debouncedChange.value ? '40px' : undefined}
                    onChange={(e) => {
                      debouncedChange.change(e.target.value);
                    }}
                    value={debouncedChange.value ?? ''}
                  />
                  {/* Même croix d'effacement que Cadencier / filtres opé co. */}
                  {!!debouncedChange.value && (
                    <InputRightElement>
                      <IconButton
                        aria-label={t('cadencier.effacer_recherche')}
                        icon={<CloseIcon boxSize={2.5} />}
                        variant='ghost'
                        size='xs'
                        color='gray.dark'
                        onClick={() => {
                          debouncedChange.change('');
                        }}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
              <Box
                pt='3'
                pb='3'
                pr={{base: '5', md: '2'}}
                pl={{base: '5', md: '2'}}
                w='100%'
              >
                <FilterBar
                  itemResults={hits ?? remplacements}
                  filters={filtersHook.params}
                  onFilterChange={(key, value) => {
                    filtersHook.change(key, value as never);
                  }}
                  onRemoveAllFilters={(preservePromo) => {
                    filtersHook.changeMultiple({
                      nature_du_prix: preservePromo ? 'promo' : undefined,
                      produit_marche: undefined,
                      labels: undefined,
                      temperature_livraison: undefined,
                      famille_fromage: undefined,
                      type_de_lait: undefined,
                      traitement_thermique_du_lait: undefined,
                    });
                  }}
                  showProduitMarche
                  // En tête en Vue Commerce seulement.
                  produitMarcheEnPremier={vueCommerce}
                  clientNav={client}
                  direction='column-reverse'
                  containerProps={{bg: 'transparent', p: '0!important'}}
                />
              </Box>
            </VStack>
          </Box>
          <Box
            flex={1}
            w='100%'
            mb='0'
            ref={boxRef}
            overflow='auto'
          >
            {listeVide ? (
              <Text
                textAlign='center'
                my='10'
                px='6'
                fontSize='lg'
                color='gray.dark'
              >
                {messageListeVide}
              </Text>
            ) : !usableWidth ? (
              <SimpleGrid
                columns={{base: 1, md: 3}}
                spacing='0'
                p='20px'
                pt={hits ? '10px' : '20px'}
                gap='0'
              >
                {placeholder}
              </SimpleGrid>
            ) : (
              <FixedSizeGrid
                rowHeight={hauteurVignette}
                columnWidth={columnWidth!}
                columnCount={columnCount}
                height={boxRef.current?.clientHeight ?? 400}
                width={usableWidth}
                rowCount={Math.max(
                  1,
                  Math.ceil(hitsToRender.length / columnCount),
                )}
              >
                {({rowIndex, columnIndex, style}) => {
                  const index = rowIndex * columnCount + columnIndex;
                  const hit = hitsToRender[index];
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  if (!hit) {
                    return null;
                  }
                  return (
                    <RemplacementItem
                      key={hit.id}
                      hit={hit.document}
                      remplacerClick={remplacerClick}
                      style={style}
                      isMobile={isMobile}
                      mode={mode}
                      isDisabled={isDisabled}
                      onProductClick={onProductClick}
                    />
                  );
                }}
              </FixedSizeGrid>
            )}
          </Box>
        </VStack>
      </DrawerContent>
    </Drawer>
  );
};

// Composant principal exporté qui récupère le client et configure le contexte SearchClient
export const ModaleRemplacementCatalogue = NiceModal.create<{
  articleData: Article;
  remplacerSubmit?: (payload: RemplacerUnArticleCommand) => void;
  mode?: ModaleRemplacementMode;
  onListAdd?: (code_article: string) => Promise<void>;
  isDisabled?: (code_article: string) => boolean;
  onProductClick?: (code_article: string) => void;
}>((props) => {
  const client = useClient();

  return (
    <SearchClientProvider client={client}>
      <ModaleRemplacementContent {...props} />
    </SearchClientProvider>
  );
});

export const RemplacementItemSkeleton = () => {
  return (
    <VStack
      height='420px'
      padding='10px'
      bg='white'
      border='1px'
      borderColor='gray.light'
      alignItems='stretch'
      position='relative'
    >
      <Skeleton
        height='20px'
        width='100%'
      />
      <Skeleton
        height='134px'
        width='100%'
        mb='10px'
      />
      <VStack width='100%'>
        <HStack
          height='22px'
          justifyContent='left'
          width='100%'
        >
          <Skeleton
            height='12px'
            width='80px'
          />
        </HStack>
        <Skeleton
          height='60px'
          width='100%'
        />
        <Skeleton
          height='10px'
          width='100%'
        />
      </VStack>
      <HStack
        justifyContent='end'
        width='100%'
        mb='1'
      >
        <VStack
          height='40px'
          alignItems='flex-end'
          gap='0'
          mt='10px'
        >
          <Skeleton
            height='20px'
            width='100px'
          />
          <Skeleton
            height='11px'
            width='80px'
          />
        </VStack>
      </HStack>
      <Skeleton
        height='40px'
        width='100%'
      />
    </VStack>
  );
};

// Changement de l'icône du bouton en fonction du mode de la modale
const modeIcon = (mode: ModaleRemplacementMode) => {
  switch (mode) {
    case 'add-to-panier':
      return <CartIcon color='blue.main' />;
    case 'add-to-cadencier':
      return (
        <CadencierIcon
          color='blue.main'
          width='19px'
          height='19px'
        />
      );
    case 'add-to-list':
      return <ListIcon color='blue.main' />;
    case 'replace':
      return <SwitchIcon color='blue.main' />;
  }
};

export const RemplacementItem: React.FC<{
  hit: HitItemCatalogue;
  remplacerClick: (itemState: State<ItemCatalogue2Desc>) => void;
  style: CSSProperties;
  isMobile: boolean;
  mode: ModaleRemplacementMode;
  isDisabled?: (code_article: string) => boolean;
  onProductClick?: (code_article: string) => void;
}> = React.memo(
  ({
    hit,
    remplacerClick,
    style,
    isMobile,
    mode,
    isDisabled,
    onProductClick,
  }) => {
    const {buildClientRoute} = useClientRoute();
    const modal = useModal();
    const code_article = hit.code;
    const itemHook = useHalState(
      useClient().follow('go:item_catalogue_2', {
        marketplace_id: 'FF',
        code: code_article,
      }),
    );
    const articleNav = useApi().follow('get-article', {code_article});

    const {t} = useTranslation();
    const client = useClient();
    const peutVoirDlcStock = usePeutVoirDlcStock();

    // Si onProductClick est défini, on ouvre la modale de détails du produit cotÃ© prise de commande
    // sinon comportement habitueÃ®Ã®Ã© cotÃ© client
    const handleItemLink = () => {
      if (onProductClick) {
        const itemNav = client.follow('go:item_catalogue_2', {
          marketplace_id: 'FF',
          code: code_article,
        });
        NiceModal.show(DetailsProduitPriseDeCommande, {itemNav});
      } else {
        modal.remove();
      }
    };
    const href = buildClientRoute(routes.client.produit, {code_article});

    const disabled = isDisabled?.(code_article);

    return (
      <HaliRenderOne
        nav={itemHook.navigable}
        skeleton={RemplacementItemSkeleton}
        render={({data: itemData, state: itemState}) => {
          const itemVignette = imageOptimisee(hit.vignette_url, 200);
          const montant_ht = itemData.prix_unitaire_ht;
          const prixBlock = montant_ht ? (
            <PrixBlockUnitaire
              prixUnitaire={montant_ht}
              prixUnitaireTtc={itemData.prix_unitaire_ttc}
              uniteFacturation={itemData.unite_de_facturation}
              tauxTVA={itemData.tva}
              fontSizeMain='xl'
              fontSizeSecondary='xs'
              // Le HStack parent pousse le bloc à droite ; alignItems start aligne
              // la TVA sous le début du montant (pas sous la fin de « € HT/unité »).
              style={{mt: '10px', minW: 0, alignItems: 'start'}}
            />
          ) : null;
          return !isMobile ? ( // Version desktop
            <VStack
              style={style}
              display={{base: 'none', md: 'flex'}}
              padding='10px'
              bg='white'
              border='1px'
              borderColor='gray.light'
              alignItems='stretch'
              position='relative'
            >
              <RemplacementItemTags
                hit={hit}
                clientNav={client}
                isMobile={isMobile}
              />
              {onProductClick ? (
                <Box
                  onClick={handleItemLink}
                  cursor='pointer'
                  style={{width: '100%', display: 'contents'}}
                >
                  <LabelDisponibilite precommande={hit.precommande} />
                  <Stack
                    maxHeight='150px'
                    flexGrow={1}
                    p='5px'
                    justifyContent='center'
                    alignItems='center'
                    mt='10px'
                  >
                    <Box
                      maxWidth='202px'
                      maxHeight='134px'
                      position='relative'
                    >
                      <Image
                        src={itemVignette}
                        alt={hit.libelle_commercial}
                        objectFit='contain'
                        width='100%'
                        height='100%'
                      />
                      <Box
                        position='absolute'
                        bottom='0'
                        left='0'
                      >
                        {hit.temperature_livraison === 'negatif' && (
                          <ColdIcon boxSize='25px' />
                        )}
                      </Box>
                    </Box>
                  </Stack>
                  <VStack
                    width='100%'
                    alignItems='stretch'
                    spacing='0'
                    flex={1}
                  >
                    <HStack
                      minH='22px'
                      alignItems='center'
                      justifyContent='start'
                    >
                      <ArticleCodesDisplay code_article={code_article} />
                      <MargesProduit
                        code_article={code_article}
                        isItemCatalogueFF
                        fontSize='10px'
                        fontWeight='600'
                      />
                      <HStack>
                        <HaliRenderOne
                          nav={articleNav}
                          render={({state: articlestate}) => {
                            return (
                              <DisplayLabels articleState={articlestate} />
                            );
                          }}
                        />
                        {/* <ListeDrapeaux listepays={pays} max={4}/> */}
                      </HStack>
                    </HStack>
                    <Text
                      align='left'
                      color='black'
                      fontSize='sm'
                      lineHeight='18px'
                      fontWeight='600'
                      noOfLines={4}
                    >
                      {hit.libelle_commercial}
                    </Text>
                    <Text
                      align='left'
                      color='black'
                      fontSize='xs'
                      lineHeight='18px'
                      h='18px'
                      mb='20px'
                      noOfLines={1}
                    >
                      {hit.marque}
                    </Text>
                    <RemplacementDisplayMultiples itemState={itemState} />
                  </VStack>
                </Box>
              ) : (
                <Link
                  to={href}
                  onClick={handleItemLink}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'contents',
                    width: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <LabelDisponibilite precommande={hit.precommande} />
                  <Stack
                    maxHeight='150px'
                    flexGrow={1}
                    p='5px'
                    justifyContent='center'
                    alignItems='center'
                    mt='10px'
                  >
                    <Box
                      maxWidth='202px'
                      maxHeight='134px'
                      position='relative'
                    >
                      <Image
                        src={itemVignette}
                        alt={hit.libelle_commercial}
                        objectFit='contain'
                        width='100%'
                        height='100%'
                      />
                      <Box
                        position='absolute'
                        bottom='0'
                        left='0'
                      >
                        {hit.temperature_livraison === 'negatif' && (
                          <ColdIcon boxSize='25px' />
                        )}
                      </Box>
                    </Box>
                  </Stack>
                  <VStack
                    width='100%'
                    alignItems='stretch'
                    spacing='0'
                    flex={1}
                  >
                    <HStack
                      minH='22px'
                      alignItems='center'
                      justifyContent='start'
                    >
                      <ArticleCodesDisplay code_article={code_article} />
                      <MargesProduit
                        code_article={code_article}
                        isItemCatalogueFF
                        fontSize='10px'
                        fontWeight='600'
                      />
                      <HaliRenderOne
                        nav={articleNav}
                        skeleton={null}
                        render={({state: articlestate}) => {
                          return <DisplayLabels articleState={articlestate} />;
                        }}
                      />
                    </HStack>
                    <VStack
                      spacing={0}
                      alignItems='start'
                    >
                      <Text
                        align='left'
                        color='black'
                        fontSize='sm'
                        lineHeight='18px'
                        fontWeight='600'
                      >
                        {hit.libelle_commercial}
                      </Text>
                      <Text
                        align='left'
                        color='black'
                        fontSize='xs'
                        lineHeight='18px'
                      >
                        {hit.marque}
                      </Text>
                      <RemplacementDisplayMultiples itemState={itemState} />
                    </VStack>
                  </VStack>
                </Link>
              )}
              {peutVoirDlcStock && (
                <HStack
                  width='100%'
                  justifyContent='flex-start'
                  alignItems='start'
                  minH='40px'
                >
                  <BoutonDLCStock
                    itemNav={itemHook.navigable}
                    itemUniteDeCommande={
                      itemState.data.unite_de_commande_client
                    }
                  />
                </HStack>
              )}
              <HStack
                justifyContent='end'
                width='100%'
                mb='3'
              >
                {prixBlock}
              </HStack>
              <Button
                onClick={() => {
                  remplacerClick(itemState);
                }}
                variant='outline'
                borderColor='blue.main'
                color='blue.main'
                leftIcon={
                  disabled ? <CheckIcon color='blue.main' /> : modeIcon(mode)
                }
                h='40px'
                isDisabled={disabled}
              >
                {disabled
                  ? mode === 'add-to-panier'
                    ? t('panier.deja_au_panier')
                    : mode === 'add-to-cadencier'
                      ? t('liste.dans_le_cadencier')
                      : t('liste.dans_la_liste')
                  : mode === 'replace'
                    ? t('global.remplacer')
                    : mode === 'add-to-panier'
                      ? t('panier.ajouter_au_panier')
                      : mode === 'add-to-cadencier'
                        ? t('liste.ajouter_au_cadencier')
                        : t('liste.ajouter_a_la_liste')}
              </Button>
            </VStack>
          ) : (
            // Version mobile
            <VStack
              style={style}
              display={{base: 'flex', md: 'none'}}
              padding='20px'
              pt='30px'
              border='1px'
              borderColor='gray.light'
              position='relative'
              bg='white'
            >
              <RemplacementItemTags
                hit={hit}
                clientNav={client}
                isMobile={isMobile}
              />
              {onProductClick ? (
                <Box
                  onClick={handleItemLink}
                  cursor='pointer'
                  style={{width: '100%', display: 'contents'}}
                >
                  <LabelDisponibilite precommande={hit.precommande} />
                  <HStack
                    height='85px'
                    width='100%'
                    mb='10px'
                    alignItems='flex-start'
                    justifyContent='flex-start'
                    spacing={4}
                  >
                    <Box
                      boxSize='90px'
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      position='relative'
                    >
                      <Image
                        src={itemVignette}
                        alt={hit.libelle_commercial}
                        objectFit='contain'
                        maxWidth='100%'
                        maxHeight='100%'
                        width='auto'
                        height='auto'
                      />
                      {hit.temperature_livraison === 'negatif' && (
                        <Box
                          position='absolute'
                          bottom='0'
                          left='0'
                          zIndex='1'
                        >
                          <ColdIcon boxSize='25px' />
                        </Box>
                      )}
                    </Box>
                    <VStack
                      alignItems='flex-start'
                      spacing={1}
                    >
                      <HStack>
                        <ArticleCodesDisplay code_article={code_article} />
                        <MargesProduit
                          code_article={code_article}
                          isItemCatalogueFF
                          fontSize='10px'
                          fontWeight='600'
                        />
                        <HaliRenderOne
                          nav={articleNav}
                          skeleton={null}
                          render={({state: articlestate}) => {
                            return (
                              <DisplayLabels articleState={articlestate} />
                            );
                          }}
                        />
                      </HStack>
                      <VStack
                        spacing={0}
                        alignItems='start'
                      >
                        <Text
                          align='left'
                          color='black'
                          fontSize='sm'
                          lineHeight='18px'
                          fontWeight='600'
                        >
                          {hit.libelle_commercial}
                        </Text>
                        <Text
                          align='left'
                          color='black'
                          fontSize='xs'
                          lineHeight='18px'
                        >
                          {hit.marque}
                        </Text>
                        <RemplacementDisplayMultiples itemState={itemState} />
                      </VStack>
                    </VStack>
                  </HStack>
                </Box>
              ) : (
                <Link
                  to={href}
                  onClick={handleItemLink}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'contents',
                    width: '100%',
                  }}
                >
                  <LabelDisponibilite precommande={hit.precommande} />
                  <HStack
                    height='85px'
                    width='100%'
                    mb='10px'
                    alignItems='flex-start'
                    justifyContent='flex-start'
                    spacing={4}
                  >
                    <Box
                      boxSize='90px'
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      position='relative'
                    >
                      <Image
                        src={itemVignette}
                        alt={hit.libelle_commercial}
                        objectFit='contain'
                        maxWidth='100%'
                        maxHeight='100%'
                        width='auto'
                        height='auto'
                      />
                      {hit.temperature_livraison === 'negatif' && (
                        <Box
                          position='absolute'
                          bottom='0'
                          left='0'
                          zIndex='1'
                        >
                          <ColdIcon boxSize='25px' />
                        </Box>
                      )}
                    </Box>
                    <VStack
                      alignItems='flex-start'
                      spacing={1}
                    >
                      <HStack>
                        <ArticleCodesDisplay code_article={code_article} />
                        <MargesProduit
                          code_article={code_article}
                          isItemCatalogueFF
                          fontSize='10px'
                          fontWeight='600'
                        />
                        <HaliRenderOne
                          nav={articleNav}
                          skeleton={null}
                          render={({state: articlestate}) => {
                            return (
                              <DisplayLabels articleState={articlestate} />
                            );
                          }}
                        />
                      </HStack>
                      <VStack
                        spacing={0}
                        alignItems='start'
                      >
                        <Text
                          align='left'
                          color='black'
                          fontSize='sm'
                          lineHeight='18px'
                          fontWeight='600'
                        >
                          {hit.libelle_commercial}
                        </Text>
                        <Text
                          align='left'
                          color='black'
                          fontSize='xs'
                          lineHeight='18px'
                        >
                          {hit.marque}
                        </Text>
                        <RemplacementDisplayMultiples itemState={itemState} />
                      </VStack>
                    </VStack>
                  </HStack>
                </Link>
              )}
              {peutVoirDlcStock && (
                <HStack
                  width='100%'
                  justifyContent='flex-start'
                  alignItems='start'
                  minH='40px'
                >
                  <BoutonDLCStock
                    itemNav={itemHook.navigable}
                    itemUniteDeCommande={
                      itemState.data.unite_de_commande_client
                    }
                  />
                </HStack>
              )}
              <HStack
                justifyContent='end'
                width='100%'
                mb='3'
              >
                {prixBlock}
              </HStack>
              <Button
                onClick={() => {
                  remplacerClick(itemState);
                }}
                variant='outline'
                borderColor='blue.main'
                color='blue.main'
                leftIcon={
                  disabled ? <CheckIcon color='blue.main' /> : modeIcon(mode)
                }
                h='40px'
                w='100%'
                isDisabled={disabled}
              >
                {disabled
                  ? mode === 'add-to-panier'
                    ? t('panier.deja_au_panier')
                    : mode === 'add-to-cadencier'
                      ? t('liste.dans_le_cadencier')
                      : t('liste.dans_la_liste')
                  : mode === 'replace'
                    ? t('global.remplacer')
                    : mode === 'add-to-panier'
                      ? t('panier.ajouter_au_panier')
                      : mode === 'add-to-cadencier'
                        ? t('liste.ajouter_au_cadencier')
                        : t('liste.ajouter_a_la_liste')}
              </Button>
            </VStack>
          );
        }}
      />
    );
  },
  (prevProps, nextProps) => {
    // verification si hit.code et hit.libelle_commercial ont changé
    return prevProps.hit.code === nextProps.hit.code;
  },
);

/**
 * Filtre les suggestions de remplacement sans quitter le périmètre famille / mot directeur.
 * Aligné sur les prédicats Orama de `searchItems` (eq / containsAll / in).
 * Les facettes sont comptées sur les suggestions retenues : la barre de filtres ne propose
 * que des choix présents dans la grille, avec leur nombre réel.
 */
function filtrerHitsRemplacement(
  results: Results<HitItemCatalogue>,
  filters: FiltresItemCatalogue,
): Results<HitItemCatalogue> {
  const hits = results.hits.filter(({document: hit}) => {
    if (filters.produit_marche === true && !hit.produit_marche) return false;
    if (
      filters.nature_du_prix &&
      hit.nature_du_prix !== filters.nature_du_prix
    ) {
      return false;
    }
    if (
      filters.labels &&
      filters.labels.length > 0 &&
      !filters.labels.every((label) => hit.labels?.includes(label))
    ) {
      return false;
    }
    if (
      filters.temperature_livraison &&
      hit.temperature_livraison !== filters.temperature_livraison
    ) {
      return false;
    }
    if (
      filters.famille_fromage &&
      filters.famille_fromage.length > 0 &&
      (!hit.famille_fromage ||
        !filters.famille_fromage.includes(hit.famille_fromage))
    ) {
      return false;
    }
    if (
      filters.type_de_lait &&
      filters.type_de_lait.length > 0 &&
      (!hit.type_de_lait || !filters.type_de_lait.includes(hit.type_de_lait))
    ) {
      return false;
    }
    if (
      filters.traitement_thermique_du_lait &&
      filters.traitement_thermique_du_lait.length > 0 &&
      (!hit.traitement_thermique_du_lait ||
        !filters.traitement_thermique_du_lait.includes(
          hit.traitement_thermique_du_lait,
        ))
    ) {
      return false;
    }
    return true;
  });
  return {
    ...results,
    hits,
    count: hits.length,
    facets: facettesDesHits(hits),
  };
}

type FacetteRemplacement =
  | 'nature_du_prix'
  | 'labels'
  | 'temperature_livraison'
  | 'produit_marche'
  | 'famille_fromage'
  | 'type_de_lait'
  | 'traitement_thermique_du_lait';

/**
 * Facettes lues par la `FilterBar`, comptées comme Orama les compte (une valeur par article,
 * chaque label d'un article compté une fois, `produit_marche` en `'true'` / `'false'`).
 */
function facettesDesHits(hits: Result<HitItemCatalogue>[]): FacetResult {
  const valeurs: Record<FacetteRemplacement, Record<string, number>> = {
    nature_du_prix: {},
    labels: {},
    temperature_livraison: {},
    produit_marche: {},
    famille_fromage: {},
    type_de_lait: {},
    traitement_thermique_du_lait: {},
  };
  const compter = (facette: FacetteRemplacement, valeur?: string) => {
    if (valeur === undefined) return;
    valeurs[facette][valeur] = (valeurs[facette][valeur] ?? 0) + 1;
  };
  for (const {document: hit} of hits) {
    compter('nature_du_prix', hit.nature_du_prix);
    new Set(hit.labels).forEach((label) => {
      compter('labels', label);
    });
    compter('temperature_livraison', hit.temperature_livraison);
    compter('produit_marche', String(hit.produit_marche ?? false));
    compter('famille_fromage', hit.famille_fromage);
    compter('type_de_lait', hit.type_de_lait);
    compter('traitement_thermique_du_lait', hit.traitement_thermique_du_lait);
  }
  return Object.fromEntries(
    Object.entries(valeurs).map(([facette, values]) => [
      facette,
      {count: Object.keys(values).length, values},
    ]),
  );
}

/**
 * Tags Promotion / Cadencier / Produit marché en haut à gauche de la vignette, comme au catalogue.
 * Un seul conteneur pour tous : positionnés chacun de leur côté, ils se recouvraient.
 * En ligne sur mobile : la vignette n'y réserve en haut (`pt='30px'`) que la hauteur
 * d'un tag, et empilés ils recouvraient l'image et le code article.
 */
const RemplacementItemTags: React.FC<{
  hit: HitItemCatalogue;
  clientNav: Navigable<ClientDesc>;
  isMobile: boolean;
}> = ({hit, clientNav, isMobile}) => {
  const {t} = useTranslation();
  return (
    <Stack
      direction={isMobile ? 'row' : 'column'}
      position='absolute'
      top={hit.precommande ? '45px' : '10px'}
      left='10px'
      align='start'
      spacing='1'
      zIndex='docked'
      pointerEvents='none'
    >
      {hit.nature_du_prix?.includes('promo') && (
        <HStack
          bg='red'
          color='white'
          fontSize='xs'
          fontWeight='bold'
          py='2px'
          px='8px'
          borderRadius='md'
          spacing='1'
        >
          <PromoIcon
            color='white'
            width='14px'
            height='14px'
          />
          <Text>{t('global.promotion')}</Text>
        </HStack>
      )}
      <TagCadencier code_article={hit.code} />
      {hit.produit_marche && (
        <HaliRenderOne
          nav={clientNav}
          skeleton={() => null}
          render={({data: clientData}) => {
            if (clientData.affichage_produit_marche === 'active') {
              return (
                <HStack
                  bg='blue.main_light'
                  color='blue.main'
                  fontSize='xs'
                  fontWeight='bold'
                  py='2px'
                  px='8px'
                  borderRadius='md'
                >
                  <Text>{t('global.produit_marche')}</Text>
                </HStack>
              );
            }
            return null;
          }}
        />
      )}
    </Stack>
  );
};

const RemplacementDisplayMultiples: React.FC<{
  itemState: State<ItemCatalogue2Desc>;
}> = ({itemState}) => {
  const {t} = useTranslation();

  const {optionsList} = useGetSelectUniteOptions({
    ic: itemState.data,
    quantite_max: undefined,
    t,
  });
  //Place le | au milieu de la liste des unités
  const optionsListText = optionsList.flatMap((opt, index) => [
    <span key={`opt-${index}`}>{opt.texte}</span>,
    index < optionsList.length - 1 ? (
      <span key={`separator-${index}`}> | </span>
    ) : null,
  ]);

  return (
    <Text
      align='left'
      color='black'
      fontSize='2xs'
      textColor='gray.dark'
    >
      {optionsListText}
    </Text>
  );
};
